import React from "react";
import {
  Card,
  CardTitle,
  CardHeader,
  CardDescription,
  CardContent,
  CardFooter,
} from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Label } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { jwtDecode } from "jwt-decode";
import { useAuth0 } from "@auth0/auth0-react";
import { requireAccessToken } from "@/lib/auth/accessToken";
import { ProfilePictureUpload } from "./components/ProfilePictureUpload";
import useUsers from "@/hooks/useUsers";
import { useNavigate } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import { handleLogout } from "@/utils/auth";
import LaraLogoIcon from "@/assets/lara/lara-icon.svg";
import lodaLogo from "@/assets/loda/loda-dark-green-logo.svg";
import * as Yup from "yup";
import { useUnleashContext } from "@unleash/proxy-client-react";
import LoadingView from "@/components/state-handlers/LoadingView";
import { Domain, getDomain } from "@/utils/domain";
import {
  TooltipContent,
  TooltipTrigger,
  Tooltip,
  TooltipProvider,
} from "@capacit-com/lara-law-shared-ui";
import { finalizeLogin } from "@/lib/finalizeLogin";
import { saveProfileOnce } from "@/lib/profileSave";
import { logger } from "@/lib/logger";

interface UserClaims {
  "https://markup.legal/name"?: string;
  "https://markup.legal/given_name"?: string;
  "https://markup.legal/family_name"?: string;
  "https://markup.legal/email"?: string;
  "https://markup.legal/picture"?: string;
  "https://markup.legal/user_id"?: string;
  "https://markup.legal/organization_id"?: string;
}

/** Site data can be blocked outright, in which case treat the user as not onboarded. */
const readOnboardedFlag = () => {
  try {
    return localStorage.getItem("onboarded") === "true";
  } catch {
    return false;
  }
};

const decodeToken = (token: string): UserClaims | null => {
  try {
    return jwtDecode<UserClaims>(token);
  } catch (error) {
    logger.error("Failed to decode access token", {
      operation: "OnboardingView.decodeToken",
      error,
    });
    return null;
  }
};

const OnboardingView: React.FC = () => {
  const navigate = useNavigate();
  const updateContext = useUnleashContext();
  const { getAccessTokenSilently, logout } = useAuth0();
  const { update, useQueryMe, onLogin } = useUsers();
  const [userClaims, setUserClaims] = React.useState<UserClaims | null>(null);
  const [loading, setLoading] = React.useState(true);
  const me = useQueryMe(true);

  React.useEffect(() => {
    const fetchTokenAndClaims = async () => {
      try {
        const token = requireAccessToken(
          await getAccessTokenSilently(),
          "OnboardingView.fetchTokenAndClaims"
        );
        const claims = decodeToken(token);
        setUserClaims(claims);
      } catch (error) {
        logger.error("Failed to fetch token or claims", {
          operation: "OnboardingView.fetchTokenAndClaims",
          error,
        });
      }
    };

    fetchTokenAndClaims();
  }, [getAccessTokenSilently]);

  // Server truth: a profile that already carries a name has been onboarded. The
  // `onboarded` localStorage flag alone re-showed this card to users whose browser
  // dropped site data, who then re-saved the same names (LAR-436).
  const isOnboardedOnServer = Boolean(me.data?.firstName && me.data?.lastName);
  const shouldSkipOnboarding = readOnboardedFlag() || isOnboardedOnServer;

  React.useEffect(() => {
    if (shouldSkipOnboarding) {
      navigate(AppRoutes.dashboard, { replace: true });
    }
  }, [shouldSkipOnboarding, navigate]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("Fornavn er påkrævet"),
    lastName: Yup.string().required("Efternavn er påkrævet"),
    email: Yup.string().email("Ugyldig email").required("Email er påkrævet"),
    profilePicture: Yup.string().optional().nullable().notRequired(),
  });

  // Email will be part of the name section of userClaims initially from Auth0, since the user was created without a name,
  // and the email is therefore defaulted. Therefore we filter this out as part of the onboarding process, and update Auth0
  // with the email as part of the onboarding flow when finalizing onboarding.
  const email = userClaims?.["https://markup.legal/email"] || "";
  const rawNameParts =
    userClaims?.["https://markup.legal/name"]?.split(" ") || [];
  const nameParts = rawNameParts.filter((part) => part !== email);
  const firstName =
    nameParts.length > 0 ? nameParts.slice(0, -1).join(" ") : null;
  const lastName =
    nameParts.length > 1 ? nameParts[nameParts.length - 1] : null;
  const initialValues = {
    firstName:
      userClaims?.["https://markup.legal/given_name"] || firstName || "",
    lastName:
      userClaims?.["https://markup.legal/family_name"] || lastName || "",
    email: email,
    profilePicture: userClaims?.["https://markup.legal/picture"] || "",
    tenantId: userClaims?.["https://markup.legal/organization_id"] || "",
  };

  // Local wrapper around the shared finalize helper
  const finalizeLoginLocal = (tenantId?: string) =>
    finalizeLogin({
      me: me.data,
      tenantId,
      updateContext,
      onLogin,
      navigate,
    });

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      await saveProfileOnce(
        {
          id: userClaims?.["https://markup.legal/user_id"] || "",
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          profilePictureUrl: values.profilePicture,
        },
        (input) => update.mutateAsync(input)
      );
      finalizeLoginLocal(values.tenantId);
    } catch (error) {
      logger.error("Failed to save onboarding profile", {
        operation: "OnboardingView.handleSubmit",
        error,
        userId: userClaims?.["https://markup.legal/user_id"],
      });
    }
  };

  // `shouldSkipOnboarding` belongs in the gate, not just in the effect: effects run
  // after the commit, so rendering the card first would flash it for a frame before
  // the redirect. Waiting for `me` keeps that decision from being made too early.
  if (loading || me.isLoading || shouldSkipOnboarding) {
    return <LoadingView />;
  }

  return (
    <div className="h-screen w-screen flex justify-center items-center flex-col gap-5">
      <img
        src={
          getDomain(window.location.hostname) === Domain.LODA
            ? lodaLogo
            : LaraLogoIcon
        }
        alt="Lara Law"
        className="w-20 h-20"
      />
      <Card className={cn("w-full max-w-md")}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Er vores information korrekt?</CardTitle>
            <Button
              type="button"
              variant={"link"}
              size={"sm"}
              onClick={() => handleLogout(logout)}
            >
              Log ud
            </Button>
          </div>
          <CardDescription>
            Vi har brug for din hjælp til at sikre, at vores information er
            korrekt
          </CardDescription>
        </CardHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="group/field grid gap-2 md:col-span-2">
                  <Label>Profilbillede</Label>
                  <ProfilePictureUpload
                    initialValue={values.profilePicture as string}
                    onChange={(value) => setFieldValue("profilePicture", value)}
                  />
                  <ErrorMessage
                    name="profilePicture"
                    component="p"
                    className="text-destructive text-sm"
                  />
                </div>

                <div className="group/field grid gap-2">
                  <Label htmlFor="firstName">
                    Fornavn <span aria-hidden="true">*</span>
                  </Label>
                  <Field
                    as={Input}
                    id="firstName"
                    name="firstName"
                    placeholder="Lee"
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="firstName"
                    component="p"
                    className="text-destructive text-sm"
                  />
                </div>
                <div className="group/field grid gap-2">
                  <Label htmlFor="lastName">
                    Efternavn <span aria-hidden="true">*</span>
                  </Label>
                  <Field
                    as={Input}
                    id="lastName"
                    name="lastName"
                    placeholder="Robinson"
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="lastName"
                    component="p"
                    className="text-destructive text-sm"
                  />
                </div>

                <div className="group/field grid gap-2 md:col-span-2">
                  <Label htmlFor="email">
                    Email <span aria-hidden="true">*</span>
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Field
                          as={Input}
                          id="email"
                          name="email"
                          placeholder="leerob@acme.com"
                          disabled={true}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Emailen kan ikke ændres. Er den forkert så kontakt en
                          administrator.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <ErrorMessage
                    name="email"
                    component="p"
                    className="text-destructive text-sm"
                  />
                </div>
              </CardContent>

              <CardFooter>
                <Button type="submit" size="sm" disabled={isSubmitting}>
                  {isSubmitting ? "Færdiggører..." : "Færdiggør onboarding"}
                </Button>
              </CardFooter>
            </Form>
          )}
        </Formik>
      </Card>
    </div>
  );
};

export default OnboardingView;
